import { createSlice } from "@reduxjs/toolkit";

import WalletKind from "./walletKind";
import WalletStatus from "./walletStatus";

import * as Metamask from "./connectors/metamask";
import * as Coinbase from "./connectors/coinbase";
import * as WalletConnect from "./connectors/walletconnect";

import * as Ether from "../shared/ether";

const initialState = {
  status: WalletStatus.idle,
  kind: null,
  account: {
    address: null,
    balance: 0,
  },
};

const savedStateName = "wallet";

const saveState = (state) => {
  const wallet = state.wallet;
  localStorage.setItem(savedStateName, JSON.stringify(wallet));
};

const preloadState = () => {
  const saved = localStorage.getItem(savedStateName);
  if (!saved) return initialState;

  const wallet = JSON.parse(saved);
  return { ...wallet, status: initialState.status };
};

const walletSlice = createSlice({
  name: "wallet",
  initialState: preloadState(),
  reducers: {
    updateAccount: (state, action) => {
      const address = action.payload.address;
      if (address) state.account.address = address;

      const balance = action.payload.balance;
      if (balance != null) state.account.balance = balance;
    },
    connecting: (state) => {
      state.status = WalletStatus.connecting;
    },
    login: (state, action) => {
      state.status = WalletStatus.connected;
      state.kind = action.payload;
    },
    logout: (state) => {
      state.status = initialState.status;
      state.account = initialState.account;
      state.kind = initialState.kind;
    },
    rejected: (state) => {
      state.status = WalletStatus.idle;
      state.account = initialState.account;
      state.kind = initialState.kind;
    },
  },
});

const { updateAccount, connecting, logout, login, rejected } = walletSlice.actions;

const connect = (wallet) => (dispatch, getState) => {
  const status = getState().wallet.status;
  if (status !== WalletStatus.idle) return;

  const onActivated = (provider) => {
    dispatch(login(wallet));
    dispatch(_subscribeOnEvents());

    saveState(getState());

    Ether.updateProvider(provider);
  };

  const onFailed = (error) => {
    console.log(error);

    dispatch(rejected());
    saveState(getState());
  };

  dispatch(connecting());

  switch (wallet) {
    case WalletKind.metamask:
      Metamask.activate().then(onActivated).catch(onFailed);
      break;

    case WalletKind.coinbase:
      Coinbase.activate().then(onActivated).catch(onFailed);
      break;

    case WalletKind.walletconnect:
      WalletConnect.activate().then(onActivated).catch(onFailed);
      break;

    default:
      console.log("Unhandled wallet provider: " + wallet);
  }
};

const reconnect = () => (dispatch, getState) => {
  const kind = getState().wallet.kind;
  if (kind) dispatch(connect(kind));
};

const disconnect = () => (dispatch, getState) => {
  const kind = getState().wallet.kind;
  switch (kind) {
    case WalletKind.coinbase:
      Coinbase.disconnect();
      break;

    case WalletKind.walletconnect:
      WalletConnect.deactivate();
      break;

    default:
      break;
  }

  Ether.updateProvider(null);

  dispatch(logout());

  saveState(getState());
};

const _subscribeOnEvents = () => (dispatch, getState) => {
  const onAccountChanged = (address) => {
    console.log("account changed: %s", address);

    if (address) {
      dispatch(updateAccount({ address: address, balance: 0 }));
    } else {
      dispatch(logout());
    }
    saveState(getState());
  };

  const kind = getState().wallet.kind;
  switch (kind) {
    case WalletKind.metamask:
      Metamask.subscribeOnEvents(onAccountChanged);
      break;

    case WalletKind.walletconnect:
      WalletConnect.subscribeOnEvents(onAccountChanged);
      break;

    default:
      break;
  }
};

const fetchAccount = () => (dispatch, getState) => {
  const kind = getState().wallet.kind;
  if (!kind) return;

  const onAccountUpdate = (address) => dispatch(updateAccount({ address: address }));

  switch (kind) {
    case WalletKind.metamask:
      Metamask.getAddress(kind).then(onAccountUpdate);
      break;

    case WalletKind.coinbase:
      Coinbase.getAddress().then(onAccountUpdate);
      break;

    case WalletKind.walletconnect:
      const address = WalletConnect.getAddress();
      onAccountUpdate(address);
      break;

    default:
      break;
  }
};

const fetchBalance = () => (dispatch, getState) => {
  const address = getState().wallet.account.address;
  if (!address) return;

  Ether.fetchBalance(address)
    .then((balance) => {
      dispatch(updateAccount({ balance: balance }));
      saveState(getState());
    })
    .catch((err) => {
      console.log(err);
    });
};

export { connect, reconnect, disconnect, fetchAccount, fetchBalance };

export default walletSlice.reducer;
